import type { Metadata } from "next";
import type { OptimizerWeightSurface } from "@/lib/optimizer-server-state";
import type { OptimizerLandingVariant } from "@/components/optimizer/optimizer-landing";
import type { PlatformType } from "@/components/optimizer/utils-v2";
import {
  FANTASY_PROPS_DEFAULT_SORT,
  normalizeQueryState,
  sanitizeFantasyPropsQueryState,
  type OptimizerQueryState,
  type OptimizerStickyFilterScope,
} from "@/components/optimizer/query-state";
import { OPTIMIZER_BOOKS, clampOptimizerState } from "@/components/optimizer/optimizer-books";
import { FANTASY_PROPS_BOOKS, clampFantasyPropsState } from "@/components/optimizer/props-books";
import type { LiveStream } from "@/lib/odds-screen/live/types";
import type { Availability } from "@/lib/book-location";
import { SPORTSBOOKS } from "@/lib/sportsbook-registry";
import {
  SPORTSBOOK_SURFACE,
  type SportsbookSurfaceId,
} from "@/lib/sportsbook-preferences";

export type OptimizerSurfaceKey = "optimizer" | "fantasy-props";

export type OptimizerSearchParams = Record<
  string,
  string | string[] | undefined
>;

interface OptimizerSurfaceStateContext {
  raw: OptimizerSearchParams;
}

export interface OptimizerSurfaceConfig {
  surface: OptimizerWeightSurface;
  sportsbookSurface: SportsbookSurfaceId;
  stickyScope: OptimizerStickyFilterScope;
  landingVariant: OptimizerLandingVariant;
  stream: LiveStream;
  metadata: {
    title(bookName: string | null): string;
    description: string;
    canonicalPath: string;
    clampForDisplay: (state: OptimizerQueryState) => OptimizerQueryState;
  };
  state: {
    prepare?: (
      state: OptimizerQueryState,
      context: OptimizerSurfaceStateContext,
    ) => OptimizerQueryState;
    afterSticky?: (
      state: OptimizerQueryState,
      raw: OptimizerSearchParams,
    ) => OptimizerQueryState;
    finalize?: (state: OptimizerQueryState) => OptimizerQueryState;
  };
  client: {
    tutorial: { videoUrl: string; toolLabel: string; toolId: string };
    allowedSportsbooks: readonly PlatformType[];
    showWeightsFilter: boolean;
  };
}

function applyFantasyPropsDefaults(
  state: OptimizerQueryState,
  raw: OptimizerSearchParams,
): OptimizerQueryState {
  const stickyResolvedState = sanitizeFantasyPropsQueryState(
    clampFantasyPropsState(state),
  );

  return {
    ...stickyResolvedState,
    sort:
      raw.sort !== undefined
        ? stickyResolvedState.sort
        : FANTASY_PROPS_DEFAULT_SORT,
    order: raw.order !== undefined ? stickyResolvedState.order : "desc",
  };
}

export const OPTIMIZER_SURFACE_CONFIGS = {
  optimizer: {
    surface: "optimizer",
    sportsbookSurface: SPORTSBOOK_SURFACE.OPTIMIZER,
    stickyScope: "optimizer",
    landingVariant: "optimizer",
    stream: "optimizer",
    metadata: {
      title: (bookName) => (bookName === null ? "Optimizer" : `${bookName} Optimizer`),
      description: "Every game line and player prop at your sportsbook, priced against fair value",
      canonicalPath: "/optimizer",
      clampForDisplay: clampOptimizerState,
    },
    state: { prepare: clampOptimizerState, finalize: clampOptimizerState },
    client: {
      tutorial: {
        videoUrl: "https://www.youtube.com/watch?v=Nemsb2fE4Fo",
        toolLabel: "Optimizer",
        toolId: "optimizer",
      },
      allowedSportsbooks: OPTIMIZER_BOOKS,
      showWeightsFilter: true,
    },
  },
  "fantasy-props": {
    surface: "fantasy-props",
    sportsbookSurface: SPORTSBOOK_SURFACE.OPTIMIZER_FANTASY_PROPS,
    stickyScope: "fantasy-props",
    landingVariant: "props",
    stream: "fantasy-props",
    metadata: {
      title: (bookName) => (bookName === null ? "Fantasy Props" : `${bookName} Fantasy Props`),
      description: "Fantasy DFS props optimizer with full league coverage",
      canonicalPath: "/optimizer/props",
      clampForDisplay: clampFantasyPropsState,
    },
    state: {
      prepare: clampFantasyPropsState,
      afterSticky: applyFantasyPropsDefaults,
      finalize: sanitizeFantasyPropsQueryState,
    },
    client: {
      tutorial: {
        videoUrl: "https://www.youtube.com/watch?v=owox0sEVRhU",
        toolLabel: "Fantasy Props",
        toolId: "fantasy-props-optimizer",
      },
      allowedSportsbooks: FANTASY_PROPS_BOOKS,
      showWeightsFilter: true,
    },
  },
} satisfies Record<OptimizerSurfaceKey, OptimizerSurfaceConfig>;

export function optimizerSurfaceTitle(config: OptimizerSurfaceConfig, book: PlatformType | null): string {
  return config.metadata.title(book === null ? null : SPORTSBOOKS[book].display);
}

export function buildOptimizerSurfaceMetadata(
  config: OptimizerSurfaceConfig,
  rawSearchParams: OptimizerSearchParams,
  here: Availability,
  defaultBookIds: readonly PlatformType[],
): Metadata {
  const queryState = config.metadata.clampForDisplay(normalizeQueryState(rawSearchParams));

  return {
    title: optimizerSurfaceTitle(config, here.shown(queryState.platform, defaultBookIds)),
    description: config.metadata.description,
    alternates: {
      canonical: config.metadata.canonicalPath,
    },
  };
}
